import React, { useEffect, useState, useMemo } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, UserPlus, Search, Layers } from "lucide-react";

// Adds EXISTING tenant users (firm or client) to a single engagement.
export function AddTeamDialog({ engId, side, open, onOpenChange, onDone, onInviteNew }) {
  const [users, setUsers] = useState([]);
  const [sel, setSel] = useState({});
  const [roleSel, setRoleSel] = useState({});
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const isClient = side === "client";

  useEffect(() => {
    if (!open) return;
    setSel({}); setRoleSel({}); setQ(""); setLoading(true);
    api.get(`/m1/engagements/${engId}/available-users?side=${side}`)
      .then((r) => setUsers(r.data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, [open, side, engId]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return users;
    return users.filter((u) => `${u.name} ${u.email}`.toLowerCase().includes(s));
  }, [users, q]);

  const chosen = Object.keys(sel).filter((k) => sel[k]);
  const add = async () => {
    if (!chosen.length) return toast.error("Select at least one person");
    setBusy(true);
    try {
      const client_roles = {};
      if (isClient) chosen.forEach((uid) => { if (roleSel[uid]) client_roles[uid] = roleSel[uid]; });
      const r = await api.post(`/m1/engagements/${engId}/add-members`, { side, user_ids: chosen, client_roles });
      toast.success(`Added ${r.data.count} member(s) to this engagement`);
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="add-team-dialog">
          <DialogHeader>
            <DialogTitle>{isClient ? "Add client team" : "Add engagement team"}</DialogTitle>
            <DialogDescription>
              {isClient
                ? "Add existing client users (the party the data is requested from). They can only see this engagement."
                : "Add firm users (the party requesting the data). They only see engagements they're added to."}
            </DialogDescription>
          </DialogHeader>

          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input data-testid="add-team-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name or email…" className="pl-9" />
          </div>

          {loading ? <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> : (
            <div className="space-y-1">
              {filtered.length === 0 && <p className="py-6 text-center text-sm text-slate-400">{users.length === 0 ? (isClient ? "No other client users available. Invite a new one below." : "No other firm users available.") : "No matches."}</p>}
              {filtered.map((u) => (
                <div key={u.id} data-testid={`add-user-${u.id}`} className="flex items-center gap-2.5 rounded-md p-2 hover:bg-slate-50 dark:hover:bg-slate-800/40">
                  <Checkbox checked={!!sel[u.id]} onCheckedChange={(v) => setSel({ ...sel, [u.id]: !!v })} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{u.name}</div>
                    <div className="truncate text-[11px] text-slate-400">{u.email}</div>
                  </div>
                  {isClient ? (
                    <Select value={roleSel[u.id] || (u.roles || []).find((r) => ["client_user", "client_admin"].includes(r)) || "client_user"} onValueChange={(v) => setRoleSel({ ...roleSel, [u.id]: v })}>
                      <SelectTrigger className="h-7 w-32 text-xs" data-testid={`add-user-role-${u.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="client_user">Client User</SelectItem>
                        <SelectItem value="client_admin">Client Admin</SelectItem>
                      </SelectContent>
                    </Select>
                  ) : (u.roles || []).slice(0, 1).map((r) => (
                    <span key={r} className="rounded px-1.5 py-0.5 text-[10px] font-semibold capitalize bg-indigo-50 dark:bg-indigo-950/40 text-indigo-600">{r.replace(/_/g, " ")}</span>
                  ))}
                </div>
              ))}
            </div>
          )}

          <DialogFooter className="flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex gap-2">
              {isClient && onInviteNew && <Button variant="ghost" size="sm" onClick={onInviteNew} data-testid="invite-new-client-btn" className="text-indigo-600"><UserPlus className="mr-1.5 h-4 w-4" /> Invite new</Button>}
              <Button variant="ghost" size="sm" onClick={() => setBulkOpen(true)} data-testid="bulk-add-btn" className="text-indigo-600"><Layers className="mr-1.5 h-4 w-4" /> Add to many engagements</Button>
            </div>
            <Button onClick={add} disabled={busy || chosen.length === 0} data-testid="add-team-submit" className="bg-indigo-600 hover:bg-indigo-700">Add {chosen.length || ""} to engagement</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BulkAddDialog side={side} open={bulkOpen} onOpenChange={setBulkOpen} onDone={() => { setBulkOpen(false); onDone(); }} />
    </>
  );
}

// Add ONE person to MANY engagements at once.
function BulkAddDialog({ side, open, onOpenChange, onDone }) {
  const [people, setPeople] = useState([]);
  const [engs, setEngs] = useState([]);
  const [userId, setUserId] = useState("");
  const [sel, setSel] = useState({});
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const isClient = side === "client";
  const teamKey = isClient ? "client_team" : "internal_team";

  useEffect(() => {
    if (!open) return;
    setUserId(""); setSel({}); setLoading(true);
    Promise.all([api.get(`/m1/tenant-users?side=${side}`), api.get("/m1/engagements?view=active")])
      .then(([u, e]) => { setPeople(u.data); setEngs(e.data); })
      .catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, [open, side]);

  const alreadyOn = (eng) => (eng[teamKey] || []).includes(userId);
  const chosen = Object.keys(sel).filter((k) => sel[k]);

  const submit = async () => {
    if (!userId) return toast.error("Pick a person");
    if (!chosen.length) return toast.error("Pick at least one engagement");
    setBusy(true);
    try {
      const r = await api.post(`/m1/users/${userId}/add-to-engagements`, { engagement_ids: chosen, side });
      toast.success(`Added to ${r.data.added} engagement(s)`);
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="bulk-add-dialog">
        <DialogHeader>
          <DialogTitle>Add one {isClient ? "client" : "team member"} to many engagements</DialogTitle>
          <DialogDescription>Pick a person, then select every engagement they should join.</DialogDescription>
        </DialogHeader>
        {loading ? <div className="flex justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-slate-400" /></div> : (
          <div className="space-y-4">
            <Select value={userId} onValueChange={(v) => { setUserId(v); setSel({}); }}>
              <SelectTrigger data-testid="bulk-user-select"><SelectValue placeholder="Select a person…" /></SelectTrigger>
              <SelectContent>
                {people.map((u) => <SelectItem key={u.id} value={u.id} data-testid={`bulk-user-${u.id}`}>{u.name} — {u.email}</SelectItem>)}
              </SelectContent>
            </Select>
            {userId && (
              <div className="space-y-1 rounded-lg border border-slate-200 dark:border-slate-800 p-2 max-h-64 overflow-y-auto">
                {engs.map((e) => {
                  const on = alreadyOn(e);
                  return (
                    <label key={e.id} data-testid={`bulk-eng-${e.id}`} className={`flex items-center gap-2.5 rounded p-2 ${on ? "opacity-50" : "hover:bg-slate-50 dark:hover:bg-slate-800/40"}`}>
                      <Checkbox checked={on || !!sel[e.id]} disabled={on} onCheckedChange={(v) => setSel({ ...sel, [e.id]: !!v })} />
                      <div className="min-w-0 flex-1"><div className="truncate text-sm font-medium">{e.name}</div><div className="truncate text-[11px] text-slate-400">{e.client_org_name}</div></div>
                      {on && <span className="text-[10px] text-slate-400">already on</span>}
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          <Button onClick={submit} disabled={busy || !userId || chosen.length === 0} data-testid="bulk-add-submit" className="bg-indigo-600 hover:bg-indigo-700">Add to {chosen.length || ""} engagement(s)</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
